import { PageBlock, StageRow } from "./pageRows.types";
import { SectionItemConfig } from "../render/contentConfig.types";

/**
 * Whether a section, field or content block is shown, given the record and the
 * organisation's config. A hidden item is not drawn, validated or saved.
 */
export type VisibilityRule = (
  record: Record<string, unknown>,
  config: Record<string, unknown>
) => boolean;

/** Rules an item can name, addressed by key so page config stays serialisable. */
export const visibilityRules: Record<string, VisibilityRule> = {};

/** Filled from each record definition's own rules, by recordDefinitions. */
export function registerVisibilityRules(
  rules: Record<string, VisibilityRule> | undefined
) {
  Object.assign(visibilityRules, rules);
}

/** True for no rule or an unknown one, so an older build still shows the item. */
export function isVisible(
  ruleKey: string | undefined,
  record: Record<string, unknown>,
  config: Record<string, unknown>
) {
  const rule = ruleKey ? visibilityRules[ruleKey] : undefined;
  return rule ? rule(record, config) : true;
}

/**
 * The rows with every item whose rule fails taken out. A column or row left with nothing
 * in it goes too, so an empty card or gap doesn't stay on screen.
 */
export function withVisibleItems(
  rows: StageRow[],
  record: Record<string, unknown>,
  config: Record<string, unknown>
): StageRow[] {
  const shown = (item: { visibleWhen?: string }) =>
    isVisible(item.visibleWhen, record, config);

  return rows
    .map((row) => ({
      ...row,
      columns: row.columns
        .map((column) => ({
          ...column,
          blocks: column.blocks.flatMap((block): PageBlock[] =>
            block.kind !== "stage"
              ? [block]
              : shown(block.stage)
              ? [
                  {
                    ...block,
                    stage: {
                      ...block.stage,
                      fields: block.stage.fields.filter((field: SectionItemConfig) =>
                        shown(field)
                      ),
                    },
                  },
                ]
              : []
          ),
        }))
        .filter(
          (column, index) =>
            column.blocks.length > 0 || row.columns[index].blocks.length === 0
        ),
    }))
    .filter(
      (row, index) => row.columns.length > 0 || rows[index].columns.length === 0
    );
}
